import * as back from '~/components/layouts/Navigation/BreadcrumbContainer/functional/backLinks';
import {
  crudBreadcrumb,
  detailBreadcrumb,
  listBreadcrumb,
  nestedCrudBreadcrumb,
  nestedDetailBreadcrumb,
  nestedListBreadcrumb,
} from '~/components/layouts/Navigation/BreadcrumbContainer/functional/generators';
import * as match from '~/components/layouts/Navigation/BreadcrumbContainer/functional/matchers';
import type { RouteRule } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/types';

const PRIORITY = {
  SECTION_BASE: 200,
  LIST_PAGE: 400,
  ADD_FORM: 500,
  EDIT_FORM: 510,
  DETAIL_PAGE: 520,
  SPECIAL_ACTION: 530,
} as const;

export const accountingRules: RouteRule[] = [
  {
    match: match.and(match.startsWith('/accounting/bank-accounts'), match.includes('/edit')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Bank accounts', '/accounting/bank-accounts', 'Edit bank account'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/bank-accounts'), match.hasId()),
    generate: detailBreadcrumb('Accounting', '/accounting', 'Bank accounts', '/accounting/bank-accounts', 'Bank account details'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/bank-accounts'), match.includes('/add')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Bank accounts', '/accounting/bank-accounts', 'Add a bank account'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/bank-accounts')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Bank accounts', '/accounting/bank-accounts'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/bills'), match.includes('/pay')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Payables', '/accounting/bills', 'Pay bills'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM + 10,
  },
  {
    match: match.and(match.startsWith('/accounting/bills'), match.includes('/edit')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Payables', '/accounting/bills', 'Edit bill'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/bills'), match.hasId()),
    generate: detailBreadcrumb('Accounting', '/accounting', 'Payables', '/accounting/bills', 'Bill details'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/bills'), match.includes('/add')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Payables', '/accounting/bills', 'Add a bill'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/bills')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Payables', '/accounting/bills'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/chart-of-accounts')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Chart of accounts', '/accounting/chart-of-accounts'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },

  {
    match: match.and(match.startsWith('/accounting/company-revenue/management-fees'), match.hasId()),
    generate: nestedDetailBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Management fees',
      '/accounting/company-revenue/management-fees',
      'Management fee detail'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/company-revenue/management-fees'), match.includes('/add')),
    generate: nestedCrudBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Management fees',
      '/accounting/company-revenue/management-fees',
      'Calculate management fee',
      '/accounting/company-revenue/management-fees/add'
    ),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/company-revenue'), match.includes('/management-fees')),
    generate: nestedListBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Management fees',
      '/accounting/company-revenue/management-fees'
    ),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 10,
  },
  {
    match: match.and(match.startsWith('/accounting/company-revenue/service-bills'), match.hasId()),
    generate: nestedDetailBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Service bills',
      '/accounting/company-revenue/service-bills',
      'Service bill detail'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/company-revenue'), match.includes('/service-bills')),
    generate: nestedListBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Service bills',
      '/accounting/company-revenue/service-bills'
    ),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 10,
  },
  {
    match: match.and(match.startsWith('/accounting/company-revenue/pay-outs'), match.includes('/calculate')),
    generate: nestedCrudBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Payouts',
      '/accounting/company-revenue/pay-outs',
      'Calculate payouts',
      '/accounting/company-revenue/pay-outs/calculate'
    ),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/accounting/company-revenue/pay-outs'), match.includes('/add')),
    generate: nestedCrudBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Payouts',
      '/accounting/company-revenue/pay-outs',
      'Add payout',
      '/accounting/company-revenue/pay-outs/add'
    ),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/company-revenue'), match.includes('/pay-outs')),
    generate: nestedListBreadcrumb(
      'Accounting',
      '/accounting',
      'Company revenue',
      '/accounting/company-revenue',
      'Payouts',
      '/accounting/company-revenue/pay-outs'
    ),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 10,
  },
  {
    match: match.and(match.startsWith('/accounting/magicdoor'), match.hasId()),
    generate: nestedDetailBreadcrumb(
      'Accounting',
      '/accounting',
      'MagicDoor',
      '/accounting/magicdoor',
      'Invoices',
      '/accounting/magicdoor',
      'Invoice details'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/magicdoor')),
    generate: listBreadcrumb('Accounting', '/accounting', 'MagicDoor', '/accounting/magicdoor'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 10,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/company-revenue')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Company revenue', '/accounting/company-revenue'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 5,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds/distributions'), match.includes('/calculate')),
    generate: nestedCrudBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Distributions',
      '/accounting/owner-funds/distributions',
      'Calculate distribution',
      '/accounting/owner-funds/distributions/calculate'
    ),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds/distributions'), match.hasId()),
    generate: nestedDetailBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Distributions',
      '/accounting/owner-funds/distributions',
      'Distribution details'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds'), match.includes('/distributions')),
    generate: nestedListBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Distributions',
      '/accounting/owner-funds/distributions'
    ),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 10,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds'), match.includes('contributions'), match.includes('/add-request')),
    generate: nestedCrudBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Contributions',
      '/accounting/owner-funds/contributions',
      'Create request',
      '/accounting/owner-funds/contributions/add-request'
    ),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds'), match.includes('contributions'), match.includes('/add')),
    generate: nestedCrudBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Contributions',
      '/accounting/owner-funds/contributions',
      'Create contribution',
      '/accounting/owner-funds/contributions/add'
    ),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds'), match.includes('contributions'), match.hasId()),
    generate: nestedDetailBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Contributions',
      '/accounting/owner-funds/contributions',
      'Contribution details'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds'), match.includes('contributions')),
    generate: nestedListBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Contributions',
      '/accounting/owner-funds/contributions'
    ),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 10,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds/transfers'), match.hasId()),
    generate: nestedDetailBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Transfers',
      '/accounting/owner-funds/transfers',
      'Transfer details'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/owner-funds'), match.includes('transfers')),
    generate: nestedListBreadcrumb(
      'Accounting',
      '/accounting',
      'Owner funds',
      '/accounting/owner-funds',
      'Transfers',
      '/accounting/owner-funds/transfers'
    ),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 10,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/owner-funds')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Owner funds', '/accounting/owner-funds'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/deposit-slips'), match.hasId(), match.includes('/edit')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Deposit slips', '/accounting/deposit-slips', 'Edit deposit slip'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM + 20,
  },
  {
    match: match.and(match.startsWith('/accounting/deposit-slips'), match.hasId()),
    generate: detailBreadcrumb('Accounting', '/accounting', 'Deposit slips', '/accounting/deposit-slips', 'Deposit slip detail'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },

  {
    match: match.and(match.startsWith('/accounting/deposit-slips'), match.includes('/add')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Deposit slips', '/accounting/deposit-slips', 'Create new deposit slip'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/deposit-slips')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Deposit slips', '/accounting/deposit-slips'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/manual-entries'), match.includes('/edit')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Manual entries', '/accounting/manual-entries', 'Edit manual entry'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/manual-entries'), match.hasId()),
    generate: detailBreadcrumb('Accounting', '/accounting', 'Manual entries', '/accounting/manual-entries', 'Manual entry detail'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/manual-entries'), match.includes('/add')),
    generate: crudBreadcrumb('Accounting', '/accounting', 'Manual entries', '/accounting/manual-entries', 'Create new manual entry'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/manual-entries')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Manual entries', '/accounting/manual-entries'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/checks')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Checks', '/accounting/checks'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting/bank-ledger-transfers'), match.includes('/edit')),
    generate: crudBreadcrumb(
      'Accounting',
      '/accounting',
      'Bank ledger transfers',
      '/accounting/bank-ledger-transfers',
      'Edit bank ledger transfer'
    ),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/bank-ledger-transfers'), match.includes('/add')),
    generate: crudBreadcrumb(
      'Accounting',
      '/accounting',
      'Bank ledger transfers',
      '/accounting/bank-ledger-transfers',
      'Create new bank ledger transfer'
    ),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/accounting/bank-ledger-transfers'), match.hasId()),
    generate: detailBreadcrumb(
      'Accounting',
      '/accounting',
      'Bank ledger transfers',
      '/accounting/bank-ledger-transfers',
      'Bank ledger transfer detail'
    ),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/accounting'), match.includes('/bank-ledger-transfers')),
    generate: listBreadcrumb('Accounting', '/accounting', 'Bank ledger transfers', '/accounting/bank-ledger-transfers'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.startsWith('/accounting'),
    generate: (context) => [{ label: context.t('Accounting') }],
    backLink: back.homeLink,
    priority: PRIORITY.SECTION_BASE,
  },
];
